import React from 'react';

import { useRecoilValue } from 'recoil';

import { getTransactionActivities } from 'api/Transaction';

import { cn } from 'lib/helpers/utility';
import useModal from 'lib/hooks/useModal';
import { isKioskDevice } from 'lib/helpers/kiosk';
import { useReceipt } from 'lib/hooks/queries/billing/useReceipt';

import { userState } from 'components/state';
import Modal from 'components/shared/Modal/Modal';
import Receipt from 'components/shared/Receipt/Receipt';
import { currentPractice } from 'components/practice/practiceState';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import { useSubModal } from 'components/shared/Modal/components/SubModal/lib/SubModalContext';
import DocumentZoomToggle from 'components/shared/DocumentSheet/components/DocumentZoomToggle';
import { DocumentZoomProvider } from 'components/shared/DocumentSheet/lib/documentZoomContext';
import { useCheckinFlowContext } from 'components/Patient/checkin/Wizard/lib/checkinFlowContext';

import Loader from '../Loader/Loader';
import ExpandableToolbar from '../ExpandableToolbar/ExpandableToolbar';

import ReceiptActionButtons from './components/ReceiptActionButtons';
import ReceiptSubModal from './components/ReceiptSubModal/ReceiptSubModal';

const ReceiptFullModal = ({
  isOpen,
  onClose,
  transactionId,
  appointmentId,
  eobId,
  viewDefaultReceipt = true,
  walkout = false,
  token,
  isPatient: isPatientProp
}) => {
  const { isOpen: isReceiptOpen, closeModal: closeReceiptModal } = useModal();

  const practice = useRecoilValue(currentPractice);
  const user = useRecoilValue(userState);
  // no printer on kiosk
  const { kiosk } = useCheckinFlowContext();
  const kioskDevice = kiosk || isKioskDevice();

  const isPatient = isPatientProp ?? (!user.logged_in || (user?.logged_in && !practice?.id));

  const { data, isLoading } = useReceipt({
    params: { transactionId, appointmentId, eobId, token },
    dependencies: [transactionId, appointmentId, eobId, token],
    options: {
      enabled:
        (isOpen || isReceiptOpen) &&
        (!!transactionId || !!appointmentId || !!eobId) &&
        viewDefaultReceipt
    }
  });

  const receipt = data?.receipt;

  const handleCloseReceipt = () => {
    closeReceiptModal();
    onClose();
  };

  return (
    <DocumentZoomProvider>
      <Modal
        isOpen={isOpen || isReceiptOpen}
        title={kioskDevice ? undefined : 'Receipt'}
        customTitle={
          kioskDevice ? (
            <span className="text-2xl font-600 leading-8 text-neutral-800">Receipt</span>
          ) : undefined
        }
        headerClassName={kioskDevice ? 'h-[72px]' : undefined}
        headerCloseIconClassName={kioskDevice ? 'size-10 min-w-10 [&_svg]:size-7' : undefined}
        handleClose={handleCloseReceipt}
        handleClick={handleCloseReceipt}
        bodyClassName="min-h-0 min-w-0 p-0 overflow-clip"
        width="large"
        headButton={
          <div className="flex w-full items-center justify-end gap-2 pr-3">
            <DocumentZoomToggle size={kioskDevice ? 'large' : 'small'} />
            <ReceiptActionButtons
              transactionId={transactionId}
              emailTransactionId={transactionId || receipt?.transaction?.id}
              appointmentId={appointmentId}
              token={token}
              channels={data?.deliveryChannels}
              kioskDevice={kioskDevice}
              isPatientSide={kioskDevice || isPatient}
            />
          </div>
        }
        isFooter={false}>
        <div className={cn('flex h-full w-full', kioskDevice && '[zoom:1.3]')}>
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <Loader color="primary" size={40} />
            </div>
          ) : (
            receipt && <Receipt receipt={receipt} walkout={walkout} isPatient={isPatient} />
          )}
          {!isLoading && !isPatient && (
            <ExpandableToolbar
              category="transaction"
              id={receipt?.transaction?.id}
              fetchData={getTransactionActivities}
            />
          )}
        </div>
      </Modal>
    </DocumentZoomProvider>
  );
};

// Inside an open shared Modal subtree the receipt presents through the host's
// SubModal; on plain pages (kiosk/check-in surfaces) it keeps the full-width Modal.
const ReceiptModal = (props) => {
  const { openSubModal } = useSubModal();

  return openSubModal ? <ReceiptSubModal {...props} /> : <ReceiptFullModal {...props} />;
};

export default withErrorBoundary(ReceiptModal);
